"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import type { EarningsCalendarItem } from "@/lib/earnings-calendar/types";
import { verificationLabels } from "@/lib/radar/labels";
import { calendarItemVerification } from "@/lib/radar/verification";
import { isTrustedCalendarItem } from "@/lib/gpw-companies/coverage";

const statusLabels: Record<EarningsCalendarItem["status"], string> = {
  planned: "Zaplanowany",
  changed: "Zmieniony",
  published: "Opublikowany",
  estimated: "Szacunkowy",
  to_verify: "Do weryfikacji",
};

function badgeClass(kind: "status" | "verification", value: string) {
  if (kind === "status") {
    if (value === "published") return "border-emerald-400/30 bg-emerald-400/10 text-emerald-100";
    if (value === "changed") return "border-[#FFD700]/35 bg-[#FFD700]/10 text-[#FFD700]";
    if (value === "to_verify") return "border-orange-300/30 bg-orange-300/10 text-orange-100";
    return "border-[#3498db]/30 bg-[#3498db]/10 text-[#9ed8ff]";
  }
  if (value === "verified") return "border-emerald-400/30 bg-emerald-400/10 text-emerald-100";
  if (value === "supported") return "border-[#3498db]/35 bg-[#3498db]/10 text-[#9ed8ff]";
  return "border-orange-300/30 bg-orange-300/10 text-orange-100";
}

function formatDate(date: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(`${date}T12:00:00`));
}

function dateInRange(date: string, range: string) {
  if (range === "all") return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${date}T00:00:00`);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);

  if (range === "today") return diffDays === 0;
  if (range === "next7") return diffDays >= 0 && diffDays <= 7;

  if (range === "week") {
    const day = today.getDay() || 7;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - day + 1);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return target >= weekStart && target <= weekEnd;
  }

  if (range === "month") {
    return target.getFullYear() === today.getFullYear() && target.getMonth() === today.getMonth();
  }

  return true;
}

function unique<T extends string | undefined>(items: T[]) {
  return [...new Set(items.filter(Boolean))] as Exclude<T, undefined>[];
}

function SourceLink({ item }: { item: EarningsCalendarItem }) {
  return (
    <a
      className="font-semibold text-[#54afe9] hover:text-white"
      href={item.sourceUrl}
      rel="nofollow noopener noreferrer"
      target="_blank"
    >
      Źródło
    </a>
  );
}

function companyPath(
  item: EarningsCalendarItem,
  companyPaths: Record<string, string>,
) {
  return item.isin ? companyPaths[`isin:${item.isin.trim().toUpperCase()}`] : undefined;
}

function CompanyLink({
  item,
  companyPaths,
  className,
}: {
  item: EarningsCalendarItem;
  companyPaths: Record<string, string>;
  className: string;
}) {
  const href = companyPath(item, companyPaths);
  if (!href) return <span className={className}>{item.companyName}</span>;

  return (
    <Link className={`${className} hover:text-[#76c5f5]`} href={href}>
      {item.companyName}
    </Link>
  );
}

export function CalendarClient({
  items,
  companyPaths,
}: {
  items: EarningsCalendarItem[];
  companyPaths: Record<string, string>;
}) {
  const [query, setQuery] = useState("");
  const [period, setPeriod] = useState("all");
  const [status, setStatus] = useState("trusted");
  const [market, setMarket] = useState("all");
  const [sector, setSector] = useState("all");
  const [sourceType, setSourceType] = useState("all");
  const [dateRange, setDateRange] = useState("all");

  const sectors = useMemo(() => unique(items.map((item) => item.sector)), [items]);
  const markets = useMemo(() => unique(items.map((item) => item.market)), [items]);
  const sourceTypes = useMemo(() => unique(items.map((item) => item.sourceType)), [items]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch =
        !search ||
        item.companyName.toLowerCase().includes(search) ||
        item.ticker?.toLowerCase().includes(search) ||
        item.isin?.toLowerCase().includes(search);

      return (
        matchesSearch &&
        (period === "all" || item.reportPeriod === period) &&
        (status === "all" || (status === "trusted" ? isTrustedCalendarItem(item) : item.status === status)) &&
        (market === "all" || item.market === market) &&
        (sector === "all" || item.sector === sector) &&
        (sourceType === "all" || item.sourceType === sourceType) &&
        dateInRange(item.reportDate, dateRange)
      );
    });
  }, [dateRange, items, market, period, query, sector, sourceType, status]);

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, EarningsCalendarItem[]>>((acc, item) => {
      acc[item.reportDate] ??= [];
      acc[item.reportDate].push(item);
      return acc;
    }, {});
  }, [filtered]);

  return (
    <section className="mt-10">
      <div className="rounded-lg border border-white/10 bg-[#101722]/85 p-5">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm font-semibold text-white">
            Szukaj spółki lub ISIN
            <input
              className="mt-2 min-h-11 w-full rounded-lg border border-white/10 bg-[#0e1117] px-3 text-sm text-white outline-none focus:border-[#3498db]"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="np. KGHM, PKO, PL..."
              value={query}
            />
          </label>
          <label className="text-sm font-semibold text-white">
            Okres
            <select className="mt-2 min-h-11 w-full rounded-lg border border-white/10 bg-[#0e1117] px-3 text-sm text-white" onChange={(event) => setPeriod(event.target.value)} value={period}>
              <option value="all">Wszystkie</option>
              <option value="Q1">Q1</option>
              <option value="H1">H1</option>
              <option value="Q3">Q3</option>
              <option value="FY">FY</option>
              <option value="Other">Inne</option>
            </select>
          </label>
          <label className="text-sm font-semibold text-white">
            Status
            <select className="mt-2 min-h-11 w-full rounded-lg border border-white/10 bg-[#0e1117] px-3 text-sm text-white" onChange={(event) => setStatus(event.target.value)} value={status}>
              <option value="trusted">Potwierdzone lub poparte źródłem</option>
              <option value="all">Wszystkie</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-white">
            Zakres dat
            <select className="mt-2 min-h-11 w-full rounded-lg border border-white/10 bg-[#0e1117] px-3 text-sm text-white" onChange={(event) => setDateRange(event.target.value)} value={dateRange}>
              <option value="all">Wszystkie</option>
              <option value="today">Dzisiaj</option>
              <option value="week">Ten tydzień</option>
              <option value="next7">Następne 7 dni</option>
              <option value="month">Ten miesiąc</option>
            </select>
          </label>
          <label className="text-sm font-semibold text-white">
            Rynek
            <select className="mt-2 min-h-11 w-full rounded-lg border border-white/10 bg-[#0e1117] px-3 text-sm text-white" onChange={(event) => setMarket(event.target.value)} value={market}>
              <option value="all">Wszystkie</option>
              {markets.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-white">
            Sektor
            <select className="mt-2 min-h-11 w-full rounded-lg border border-white/10 bg-[#0e1117] px-3 text-sm text-white" onChange={(event) => setSector(event.target.value)} value={sector}>
              <option value="all">Wszystkie</option>
              {sectors.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-white">
            Źródło
            <select className="mt-2 min-h-11 w-full rounded-lg border border-white/10 bg-[#0e1117] px-3 text-sm text-white" onChange={(event) => setSourceType(event.target.value)} value={sourceType}>
              <option value="all">Wszystkie</option>
              {sourceTypes.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end">
            <p className="text-sm text-slate-300">
              Widoczne wpisy: <span className="font-semibold text-white">{filtered.length}</span>
            </p>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.035] p-6">
          <h2 className="text-2xl font-semibold text-white">Brak wpisów dla wybranych filtrów</h2>
          <p className="mt-3 text-sm leading-7 text-slate-300">
            Nie znaleziono jeszcze terminów w automatycznych źródłach albo filtry są zbyt wąskie.
            Kalendarz uzupełni się po kolejnym imporcie komunikatów ESPI/EBI.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link className="rounded-lg border border-[#3498db]/40 px-4 py-3 text-sm font-semibold text-[#9ed8ff]" href="/barometr-gpw-q1-2026">
              Pobierz Barometr GPW Q1 2026
            </Link>
            <Link className="rounded-lg border border-white/15 px-4 py-3 text-sm font-semibold text-white" href="/jak-czytac-wyniki-spolek">
              Zobacz, jak czytać wyniki spółek
            </Link>
          </div>
        </div>
      ) : null}

      <div className="mt-6 hidden overflow-x-auto rounded-lg border border-white/10 lg:block">
        <table className="min-w-full border-collapse bg-[#101722]/85 text-left text-sm">
          <thead className="bg-white/[0.06] text-xs uppercase tracking-[0.14em] text-slate-300">
            <tr>
              <th className="border-b border-white/10 px-4 py-3">Data</th>
              <th className="border-b border-white/10 px-4 py-3">Spółka</th>
              <th className="border-b border-white/10 px-4 py-3">Okres</th>
              <th className="border-b border-white/10 px-4 py-3">Status</th>
              <th className="border-b border-white/10 px-4 py-3">Weryfikacja</th>
              <th className="border-b border-white/10 px-4 py-3">Źródło</th>
              <th className="border-b border-white/10 px-4 py-3">Weryfikacja</th>
              <th className="border-b border-white/10 px-4 py-3">Notatki</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr className="border-b border-white/10 last:border-b-0" key={item.id}>
                <td className="min-w-36 px-4 py-4 align-top text-slate-200">{formatDate(item.reportDate)}</td>
                <td className="min-w-64 px-4 py-4 align-top">
                  <CompanyLink className="font-semibold text-white" companyPaths={companyPaths} item={item} />
                  <p className="mt-1 text-xs text-slate-400">{item.ticker || item.isin || item.market}</p>
                </td>
                <td className="px-4 py-4 align-top text-slate-300">{item.reportPeriod}</td>
                <td className="px-4 py-4 align-top">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${badgeClass("status", item.status)}`}>
                    {statusLabels[item.status]}
                  </span>
                </td>
                <td className="px-4 py-4 align-top">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${badgeClass("verification", calendarItemVerification(item))}`}>
                    {verificationLabels[calendarItemVerification(item)]}
                  </span>
                </td>
                <td className="px-4 py-4 align-top">
                  <SourceLink item={item} />
                  <p className="mt-1 text-xs text-slate-400">{item.sourceType}</p>
                </td>
                <td className="px-4 py-4 align-top text-xs text-slate-400">{item.lastVerifiedAt.slice(0, 10)}</td>
                <td className="min-w-56 px-4 py-4 align-top text-xs leading-5 text-slate-400">{item.notes || item.detectedFromTitle}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid gap-4 lg:hidden">
        {filtered.map((item) => (
          <article className="rounded-lg border border-white/10 bg-[#101722]/85 p-5" key={item.id}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-[#FFD700]">{formatDate(item.reportDate)}</p>
                <h3 className="mt-2 text-lg font-semibold text-white">
                  <CompanyLink className="text-white" companyPaths={companyPaths} item={item} />
                </h3>
                <p className="mt-1 text-xs text-slate-400">{item.ticker || item.isin || item.market}</p>
              </div>
              <p className="rounded-full border border-white/10 px-2.5 py-1 text-xs text-slate-300">{item.reportPeriod}</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${badgeClass("status", item.status)}`}>
                {statusLabels[item.status]}
              </span>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${badgeClass("verification", calendarItemVerification(item))}`}>
                {verificationLabels[calendarItemVerification(item)]}
              </span>
              <span className="rounded-full border border-white/10 px-2.5 py-1 text-xs text-slate-300">
                {item.sourceType}
              </span>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-300">{item.notes || item.detectedFromTitle}</p>
            <div className="mt-4 flex items-center justify-between gap-4 text-xs text-slate-400">
              <SourceLink item={item} />
              <span>Weryfikacja: {item.lastVerifiedAt.slice(0, 10)}</span>
            </div>
          </article>
        ))}
      </div>

      {Object.keys(grouped).length > 0 ? (
        <section className="mt-12">
          <h2 className="text-2xl font-semibold text-white">Widok według dat</h2>
          <div className="mt-5 grid gap-4">
            {Object.entries(grouped).map(([date, group]) => (
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-5" key={date}>
                <h3 className="text-lg font-semibold text-white">{formatDate(date)}</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {group.map((item) => (
                    companyPath(item, companyPaths) ? (
                      <Link className="rounded-full border border-white/10 bg-[#101722] px-3 py-1 text-xs text-slate-200 hover:border-[#3498db]/50 hover:text-white" href={companyPath(item, companyPaths)!} key={item.id}>
                        {item.companyName} · {item.reportPeriod} · {statusLabels[item.status]}
                      </Link>
                    ) : (
                      <span className="rounded-full border border-white/10 bg-[#101722] px-3 py-1 text-xs text-slate-200" key={item.id}>
                        {item.companyName} · {item.reportPeriod} · {statusLabels[item.status]}
                      </span>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </section>
  );
}
